import { useEffect, useMemo, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";
import { GridRowSelectionModel } from "@mui/x-data-grid";

import { Box, Paper } from "@mui/material";
import Loader from "../Loader";

import AddArticleModal from "./AddArticleModal";
import EditArticleModal from "./EditArticleModal";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "../EnhancedToolbar";

import { Article, Container } from "types";
import { ARTICLES_URL as BASE_URL, CONTAINERS_URL } from "constants/urls";
import { ENTITY } from "constants/entities";

import useModals from "hooks/useModals";
import useCrud from "hooks/useCrud";
import { STATUS_CODES } from "constants/index";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useFilter } from "hooks/useFilter";
import { getUserFullName } from "helpers";
import { getClientName } from "helpers/getClientName";
import { exportToExcel } from "helpers/exportToExcel";

export default function ArticlesList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();

  const {
    loading,
    firstLoad,
    updateData: updateArticle,
    deleteData: deleteArticle,
    createData: createArticle,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Article>(BASE_URL);

  const { createData: createContainer, updateData: updateContainer } =
    useCrud<Container>(CONTAINERS_URL, {
      disableAutoFetch: true,
    });

  const {
    addSessionData,
    addMultipleSessionData,
    updateSessionData,
    updateMultipleSessionData,
    deleteSessionData,
    articles,
    containers,
    clients,
    units,
    groups,
    users,
  } = useSession();

  const articlesExcelColumns = {
    Codigo: (a: Article) => a.code ?? "-",
    Descripción: (a: Article) => a.description ?? "-",
    Stock: (a: Article) => a.amount ?? 0,
    Peso: (a: Article) => a.weight ?? 0,
    Ancho: (a: Article) => a.width ?? "-",
    Alto: (a: Article) => a.height ?? "-",
    Largo: (a: Article) => a.long ?? "-",
    "Cantidad por Contenedor": (a: Article) => a.amount_per_container ?? "-",
    "Unidad Operativa": (a: Article) => a.unit?.description ?? "-",
    "Presentación": (a: Article) => a.presentation ?? "-",
    "Unidades por pallet": (a: Article) => a.units_per_pallet ?? 0,
    Contenedor: (a: Article) => a.container?.description ?? "-",
    Cliente: (a: Article) => getClientName(a.client) ?? "-",
    Observaciones: (a: Article) => a.observation?.trim() || "-",
    "Requiere FV": (a: Article) => (a.require_fecha_vencimiento ? "Sí" : "No"),
    "Cantidad por contenedor estándar": (a: Article) =>
      a.is_standard_amount_per_container ? "Sí" : "No",
    Grupo: (a: any) => a.group_codes ?? "-",
    "Creado por": (a: Article & { created_by_name?: string }) =>
      a.created_by_name ?? "-",
    FCE: (a: Article) => a.fce ?? 1,
  } satisfies Record<string, (row: Article) => any>;

  const handleExportArticlesExcel = () => {
    exportToExcel<Article>({
      data: filteredArticles,
      columns: articlesExcelColumns,
      sheetName: "Artículos",
      fileNamePrefix: "Articulos",
      headerRows: [
        ["Listado de Artículos"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const enrichedArticles = useMemo(() => {
    const groupsById = new Map(groups.map((g) => [g.id, g]));

    return articles.map((article) => {
      const groupCodes = article.GroupArticles?.length
        ? article.GroupArticles.map((ga) => groupsById.get(ga.group_id)?.code)
            .filter(Boolean)
            .join(", ")
        : "-";

      return {
        ...article,
        client_name: getClientName(article.client),
        group_codes: groupCodes,
        created_by_name: (article as Article & { created_by?: number | null })
          .created_by
          ? getUserFullName(
              (article as Article & { created_by?: number | null }).created_by,
              users,
            )
          : "SISTEMA",
      };
    });
  }, [articles, groups, users]);

  const {
    filterText,
    setFilterText,
    filteredData: filteredArticles,
  } = useFilter<
    Article & {
      client_name: string;
      group_codes: string;
      created_by_name: string;
    }
  >({
    data: enrichedArticles,
    fields: gridStructure[ENTITY.ARTICLES].fields.map(({ field }) =>
      field === "client" ? { field: "client_name" } : { field },
    ),
  });

  const { showSnackBar } = useToast();

  const handleCreateContainer = async (
    newContainer: Container,
  ): Promise<{ status: number; data: Container }> => {
    const response = await createContainer(newContainer, true, true);

    if (response.status === STATUS_CODES.OK && response.data) {
      addSessionData(ENTITY.CONTAINERS, response.data as Container);
      return { status: response.status, data: response.data as Container };
    } else {
      throw new Error("Error al crear el contenedor.");
    }
  };

  const handleDeleteSelected = async () => {
    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteArticle(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.ARTICLES,
      selected
        .filter((item) => !failedDeletes.includes(Number(item)))
        .map((item) => item.toString()),
    );

    setSelected([]);
  };

  const checkSelectedArticlesStock = (): boolean => {
    let canDelete = true;
    selected.map((item) => {
      const value = articles.find((a) => a.id === item)?.amount;
      if (value && value > 0) canDelete = false;
    });

    return canDelete;
  };

  const handleCreate = async (
    newArticle: Article,
    hasNewContainer: boolean,
    selectedGroups: number[],
  ) => {
    const response = await createArticle(
      {
        ...newArticle,
        GroupArticles: selectedGroups.map((selectedGroup) => {
          return {
            group_id: selectedGroup,
          };
        }),
      },
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      hasNewContainer
        ? addMultipleSessionData(
            [ENTITY.ARTICLES, ENTITY.CONTAINERS],
            [response.data as Article, newArticle.container as Container],
          )
        : addSessionData(ENTITY.ARTICLES, response.data as Article);
    }
  };

  const handleEdit = async (
    articleToEdit: Article,
    selectedGroups: number[],
  ) => {
    const response = await updateArticle(
      articleToEdit.id,
      {
        ...articleToEdit,
        GroupArticles: selectedGroups.map((selectedGroup) => {
          return {
            group_id: selectedGroup,
          };
        }),
      },
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      updateSessionData(ENTITY.ARTICLES, response.data as Article);
    }
    setSelected([]);
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <AddArticleModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
            containers={containers}
            clients={clients}
            units={units}
            groups={groups}
            onNewContainerCreate={handleCreateContainer}
          />
          <DeleteConfirmationModaL
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={"Artículos"}
            canDelete={checkSelectedArticlesStock()}
            values={selected.map((item) => {
              const value = articles.find((a) => a.id === item)?.description;
              if (value) return value;
              return "";
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditArticleModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={articles.find((a) => a.id === selected[0])}
              onEdit={handleEdit}
              clients={clients}
              units={units}
              containers={containers}
              groups={groups}
            />
          ) : null}
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                onFilterChange={setFilterText}
                onExportExcel={handleExportArticlesExcel}
              />
              <CustomGrid<Article>
                structure={gridStructure[ENTITY.ARTICLES]}
                rows={filteredArticles}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
